import React, { useEffect, useRef, useState } from "react";

const FILL_DURATION = 1800;
const HOLD_BEFORE_EXIT = 200;
const FADE_DURATION = 380;
const IRIS_DURATION = 680;

export default function Loading({ isLoading, onLoadComplete }) {
  const [progress, setProgress] = useState(0);
  const [phase, setPhase] = useState("fill"); // fill | fade | iris | done
  const startRef = useRef(null);
  const rafRef = useRef(null);
  const doneRef = useRef(false);
  const circleRef = useRef(null);

  /* ── progress fill ── */
  useEffect(() => {
    if (!isLoading || doneRef.current) return;

    const tick = (now) => {
      if (!startRef.current) startRef.current = now;
      const t = Math.min((now - startRef.current) / FILL_DURATION, 1);
      setProgress(t);

      if (t < 1) {
        rafRef.current = requestAnimationFrame(tick);
      } else {
        setTimeout(() => {
          doneRef.current = true;
          setPhase("fade");
          setTimeout(() => {
            setPhase("iris");
            setTimeout(() => {
              setPhase("done");
              onLoadComplete && onLoadComplete();
            }, IRIS_DURATION);
          }, FADE_DURATION);
        }, HOLD_BEFORE_EXIT);
      }
    };

    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading]);

  /* ── drive iris collapse via rAF ── */
  useEffect(() => {
    if (phase !== "iris") return;
    const start = performance.now();

    /* ease-in-out cubic */
    const ease = (t) => t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;

    const MAX_R = Math.hypot(window.innerWidth, window.innerHeight) / 2 + 10;

    const tick = (now) => {
      const t = Math.min((now - start) / IRIS_DURATION, 1);
      const r = MAX_R * (1 - ease(t));
      if (circleRef.current) circleRef.current.setAttribute("r", r);
      if (t < 1) requestAnimationFrame(tick);
    };

    requestAnimationFrame(tick);
  }, [phase]);

  if (phase === "done") return null;

  const pct = Math.round(progress * 100);
  const display = String(pct).padStart(3, "0");
  const contentFading = phase === "fade" || phase === "iris";

  /* initial circle radius: large enough to cover any viewport */
  const MAX_R_INIT = typeof window !== "undefined"
    ? Math.hypot(window.innerWidth, window.innerHeight) / 2 + 10
    : 2000;

  return (
    <div
      aria-hidden="true"
      style={{ position: "fixed", inset: 0, zIndex: 999999 }}
    >
      {/* ── SVG iris ── */}
      <svg
        style={{ position: "absolute", inset: 0, width: "100%", height: "100%", overflow: "visible" }}
      >
        <defs>
          {/*
            Clip path = a circle that shrinks to 0.
            The bg rect is clipped to this circle → looks like an iris closing.
          */}
          <clipPath id="iris-clip" clipPathUnits="userSpaceOnUse">
            <circle
              ref={circleRef}
              cx={typeof window !== "undefined" ? window.innerWidth / 2 : 960}
              cy={typeof window !== "undefined" ? window.innerHeight / 2 : 540}
              r={MAX_R_INIT}
            />
          </clipPath>
        </defs>

        <rect
          x="0" y="0" width="100%" height="100%"
          fill="#F0EDE8"
          clipPath={phase === "iris" ? "url(#iris-clip)" : undefined}
        />
      </svg>

      {/* ── content (fades before iris) ── */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          opacity: contentFading ? 0 : 1,
          transition: phase === "fade" ? `opacity ${FADE_DURATION}ms ease` : "none",
          pointerEvents: "none",
        }}
      >
        {/* large counter — dead center */}
        <div style={{
          position: "absolute",
          inset: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}>
          <span style={{
            // fontFamily: "'Cabinet Grotesk', sans-serif",
            fontWeight: 900,
            fontSize: "clamp(5rem, 22vw, 22vw)",
            letterSpacing: "-0.06em",
            lineHeight: 1,
            color: "#0A0A08",
            userSelect: "none",
            fontVariantNumeric: "tabular-nums",
          }}
            className="font-Jakarta"
          >
            {display} %
          </span>
        </div>

        {/* bottom strip: progress bar + percentage */}
        <div style={{
          position: "absolute",
          bottom: 0, left: 0, right: 0,
          padding: "clamp(24px, 4vw, 48px) clamp(24px, 5vw, 64px)",
          // borderTop: "1px solid rgba(245,244,240,0.07)",
          display: "flex",
          alignItems: "center",
          gap: "20px",
        }}>
          <div style={{ flex: 1, position: "relative", height: "1px" }}>
            <div style={{ width: "100%", height: "1px", background: "rgba(10,10,8,0.12)" }} />
            <div style={{
              position: "absolute",
              top: 0, left: 0, height: "4px",
              width: `${pct}%`,
              background: "#0A0A08",
            }} />
          </div>
          {/* <span style={{
            fontFamily: "'Montreal Mono', monospace",
            fontSize: "11px",
            letterSpacing: "0.14em",
            textTransform: "uppercase",
            color: "rgba(245,244,240,0.30)",
            fontVariantNumeric: "tabular-nums",
            whiteSpace: "nowrap",
            minWidth: "4ch",
            textAlign: "right",
          }}>
            {pct}%
          </span> */}
        </div>
      </div>
    </div>
  );
}
